import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, ArrowLeft } from 'lucide-react';
import apiService from '../../services/api';
import { Product } from '../../types';
import { getPricingConfig, PRICING_DEFAULTS, savePricingConfig } from '../../utils/pricingConfig';
import { normalizeProductImageInput, toProductImageUrl } from '../../utils/imageUrl';

type ColorKey = 'negro' | 'blanco' | 'azul' | 'verde' | 'rojo';

const getUnitsPerPackFromName = (name: string) => {
  const match = String(name || '').match(/x\s*(\d+)\s*unidades?/i);
  const units = match ? Number(match[1]) : 0;
  return Number.isFinite(units) && units > 0 ? units : 0;
};

const NewProduct: React.FC = () => {
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [error, setError] = useState('');
  const [colorCalc, setColorCalc] = useState<ColorKey>('negro');
  const [preciosPorColor, setPreciosPorColor] = useState<Record<ColorKey, number>>(PRICING_DEFAULTS.preciosPorColor);
  const [bolsasPorPackCalc, setBolsasPorPackCalc] = useState(10);
  const [valorDolar, setValorDolar] = useState(PRICING_DEFAULTS.valorDolar);
  const [autoPrecio, setAutoPrecio] = useState(true);
  const [precioARSInput, setPrecioARSInput] = useState('0');
  const [form, setForm] = useState({
    name: '',
    description: '',
    category: 'comercial',
    material: 'LDPE',
    thickness: 20,
    width: 30,
    length: 40,
    priceUSD: 0,
    minQuantity: 1000,
    deliveryDays: 0,
    image: '',
    colors: '',
    isFeatured: false,
    isActive: true,
    showPrice: false
  });

  const onChange = (field: string, value: string | number | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const parseDecimal = (value: string | number) => {
    if (typeof value === 'number') return value;
    return Number(String(value).replace(',', '.')) || 0;
  };

  const unitsPerPack = useMemo(
    () => getUnitsPerPackFromName(form.name) || Number(bolsasPorPackCalc) || 1,
    [form.name, bolsasPorPackCalc]
  );

  useEffect(() => {
    const syncPricing = async () => {
      const cfg = getPricingConfig();
      setPreciosPorColor(cfg.preciosPorColor);
      setValorDolar(cfg.valorDolar);

      try {
        const currency = await apiService.getCurrencyRates();
        const sell = Number(currency?.rates?.sell || 0);
        if (Number.isFinite(sell) && sell > 0 && sell !== cfg.valorDolar) {
          savePricingConfig({ ...cfg, valorDolar: sell });
          setValorDolar(sell);
        }
      } catch {
        // Si falla la consulta, se usa el valor guardado.
      }
    };

    syncPricing();
  }, []);

  const calculo = useMemo(() => {
    const anchoCm = parseDecimal(form.width) || 0;
    const largoCm = parseDecimal(form.length) || 0;
    const micrones = Number(form.thickness) || 0;
    const cantidadMinimaPacks = Number(form.minQuantity) || 0;
    const costoKg = Number(preciosPorColor[colorCalc]) || 0;
    const bolsasPorPack = Number(bolsasPorPackCalc) || 10;
    const cantidadPacks = Math.max(1, cantidadMinimaPacks);

    const peso1000m = ((0.92 * anchoCm * micrones) / 100) * 2;
    const pesoBolsaG = (largoCm / 100) * peso1000m;
    const pesoPackG = pesoBolsaG * bolsasPorPack;
    const pesoTotalKg = (pesoPackG * cantidadPacks) / 1000;
    const factorPack = pesoPackG > 0 && pesoPackG < 200 ? 1.25 : 1;

    let mult = 4;
    let rango = 'Producto final (<10 kg)';
    if (pesoTotalKg >= 200) {
      mult = 1.5;
      rango = 'Revendedor mayorista (>=200 kg)';
    } else if (pesoTotalKg >= 100) {
      mult = 1.6;
      rango = 'Revendedor (100-199.99 kg)';
    } else if (pesoTotalKg >= 50) {
      mult = 1.75;
      rango = 'Revendedor (50-99.99 kg)';
    } else if (pesoTotalKg >= 20) {
      mult = 2;
      rango = 'Revendedor (20-49.99 kg)';
    } else if (pesoTotalKg >= 10) {
      mult = 2.5;
      rango = 'Revendedor (10-19.99 kg)';
    }

    const descuentoPct = pesoTotalKg >= 20 ? Math.round((1 - mult / 2.5) * 100) : 0;
    const margenPct = Math.round(((factorPack * mult) - 1) * 100);
    const costoPedidoUSD = pesoTotalKg * costoKg;
    const precioTotalUSD = costoPedidoUSD * factorPack * mult;
    const precioSugeridoPackUSD = cantidadPacks > 0 ? (precioTotalUSD / cantidadPacks) : 0;
    const precioSugeridoPackARS = precioSugeridoPackUSD * (Number(valorDolar) || 0);

    return {
      pesoBolsaG,
      pesoPackG,
      pesoTotalKg,
      factorPack,
      rango,
      descuentoPct,
      margenPct,
      precioSugeridoPackUSD,
      precioSugeridoPackARS
    };
  }, [form.width, form.length, form.thickness, form.minQuantity, colorCalc, preciosPorColor, bolsasPorPackCalc, valorDolar]);

  useEffect(() => {
    if (!autoPrecio) return;
    const sugeridoARS = Number(calculo.precioSugeridoPackARS.toFixed(2));
    setPrecioARSInput(String(sugeridoARS));
    const usd = (Number(valorDolar) || 0) > 0
      ? sugeridoARS / Number(valorDolar) / unitsPerPack
      : 0;
    setForm((prev) => ({ ...prev, priceUSD: Number(usd.toFixed(6)) }));
  }, [autoPrecio, calculo.precioSugeridoPackARS, valorDolar, unitsPerPack]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSaving(true);

    try {
      const payload: Partial<Product> = {
        ...form,
        image: normalizeProductImageInput(form.image),
        priceUSD: (Number(valorDolar) || 0) > 0
          ? Number((parseDecimal(precioARSInput) / Number(valorDolar) / unitsPerPack).toFixed(6))
          : 0,
        category: form.category as Product['category'],
        width: form.width / 100,
        length: form.length / 100,
        colors: form.colors
          ? form.colors.split(',').map((c) => c.trim()).filter(Boolean)
          : []
      };

      await apiService.createProduct(payload);
      navigate('/admin/productos');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'No se pudo crear el producto');
    } finally {
      setIsSaving(false);
    }
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    setIsUploadingImage(true);
    try {
      const uploaded = await apiService.uploadProductImage(file);
      onChange('image', uploaded.imageUrl);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'No se pudo subir la imagen');
    } finally {
      setIsUploadingImage(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Nuevo Producto</h1>
          <p className="text-gray-600">Carga un producto real al catalogo</p>
        </div>
        <button
          onClick={() => navigate('/admin/productos')}
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Volver
        </button>
      </div>

      <form onSubmit={onSubmit} className="bg-white rounded-lg shadow p-6 space-y-4">
        {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">{error}</div>}

        <div className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded p-3">
          Completa los campos con estos formatos: ancho/largo en centimetros (ej: 30), grosor en micrones, precio por pack en ARS.
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-blue-50 border border-blue-200 rounded p-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Color (cotizador)</label>
            <select
              className="border rounded-lg px-3 py-2 w-full"
              value={colorCalc}
              onChange={(e) => setColorCalc(e.target.value as ColorKey)}
            >
              <option value="negro">Negro</option>
              <option value="blanco">Blanco</option>
              <option value="azul">Azul</option>
              <option value="verde">Verde</option>
              <option value="rojo">Rojo</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">USD/kg del color</label>
            <input
              className="border rounded-lg px-3 py-2 w-full bg-gray-50"
              type="number"
              step="0.01"
              min={0}
              value={preciosPorColor[colorCalc]}
              readOnly
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Bolsas por pack (calculo)</label>
            <input
              className="border rounded-lg px-3 py-2 w-full"
              type="number"
              step="1"
              min={1}
              value={bolsasPorPackCalc}
              onChange={(e) => setBolsasPorPackCalc(Number(e.target.value))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Valor dolar (ARS)</label>
            <input
              className="border rounded-lg px-3 py-2 w-full bg-gray-50"
              type="number"
              step="1"
              min={1}
              value={valorDolar}
              readOnly
            />
          </div>
          <div className="text-sm text-gray-700 flex items-end">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={autoPrecio} onChange={(e) => setAutoPrecio(e.target.checked)} />
              Auto aplicar precio sugerido
            </label>
          </div>
          <div className="text-sm"><span className="font-semibold">Rango:</span> {calculo.rango}</div>
          <div className="text-sm"><span className="font-semibold">% Descuento:</span> {calculo.descuentoPct}%</div>
          <div className="text-sm"><span className="font-semibold">% Margen:</span> {calculo.margenPct}%</div>
          <div className="text-sm"><span className="font-semibold">Peso por bolsa:</span> {calculo.pesoBolsaG.toFixed(2)} g</div>
          <div className="text-sm"><span className="font-semibold">Peso por pack:</span> {calculo.pesoPackG.toFixed(2)} g {calculo.factorPack > 1 ? '(+25%)' : ''}</div>
          <div className="text-sm"><span className="font-semibold">Peso total:</span> {calculo.pesoTotalKg.toFixed(2)} kg</div>
          <div className="text-sm md:col-span-3">
            <span className="font-semibold">Precio sugerido por pack (ARS, segun cantidad minima):</span> {calculo.precioSugeridoPackARS.toFixed(2)}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Imagen del producto</label>
            <input className="border rounded-lg px-3 py-2 w-full" type="file" accept="image/*" onChange={handleImageChange} />
            {isUploadingImage && <p className="text-sm text-gray-500 mt-1">Subiendo imagen...</p>}
            <div className="my-2 flex items-center gap-3 text-xs text-gray-500">
              <span className="h-px flex-1 bg-gray-200" />
              o usar un enlace externo
              <span className="h-px flex-1 bg-gray-200" />
            </div>
            <input
              className="border rounded-lg px-3 py-2 w-full"
              type="url"
              inputMode="url"
              placeholder="https://ejemplo.com/imagen-producto.jpg"
              value={form.image.startsWith('http://') || form.image.startsWith('https://') ? form.image : ''}
              onChange={(e) => onChange('image', e.target.value.trim())}
              onBlur={(e) => onChange('image', normalizeProductImageInput(e.target.value))}
            />
            <p className="mt-1 text-xs text-gray-500">El enlace no ocupa espacio en este servidor. Se recomienda usar HTTPS.</p>
            {form.image && (
              <div className="mt-2 flex items-start gap-3">
                <img src={toProductImageUrl(form.image)} alt="Vista previa" className="h-28 w-28 object-cover rounded border" />
                <button type="button" onClick={() => onChange('image', '')} className="text-sm text-red-600 hover:text-red-700">
                  Quitar imagen
                </button>
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nombre del producto</label>
            <input className="border rounded-lg px-3 py-2 w-full" placeholder="Ej: Residuos 45x60 x10 unidades" value={form.name} onChange={(e) => onChange('name', e.target.value)} required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Categoria</label>
            <select className="border rounded-lg px-3 py-2 w-full" value={form.category} onChange={(e) => onChange('category', e.target.value)}>
              <option value="comercial">Comercial</option>
              <option value="industrial">Industrial</option>
              <option value="domestico">Domestico</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Material</label>
            <input className="border rounded-lg px-3 py-2 w-full" placeholder="Ej: LDPE" value={form.material} onChange={(e) => onChange('material', e.target.value)} required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Grosor (micrones)</label>
            <input className="border rounded-lg px-3 py-2 w-full" type="number" min={1} max={200} placeholder="Ej: 20" value={form.thickness} onChange={(e) => onChange('thickness', Number(e.target.value))} required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Ancho (cm, max 120)</label>
            <input className="border rounded-lg px-3 py-2 w-full" type="number" step="1" min={1} max={120} placeholder="Ej: 30" value={form.width} onChange={(e) => onChange('width', Number(e.target.value))} required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Largo (cm, max 120)</label>
            <input className="border rounded-lg px-3 py-2 w-full" type="number" step="1" min={1} max={120} placeholder="Ej: 40" value={form.length} onChange={(e) => onChange('length', Number(e.target.value))} required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Bolsas por pack</label>
            <input
              className="border rounded-lg px-3 py-2 w-full"
              type="number"
              step="1"
              min={1}
              value={bolsasPorPackCalc}
              onChange={(e) => setBolsasPorPackCalc(Number(e.target.value))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Precio por pack (ARS)</label>
            <input
              className="border rounded-lg px-3 py-2 w-full"
              type="text"
              inputMode="decimal"
              placeholder="Ej: 89,30"
              value={precioARSInput}
              onChange={(e) => {
                setPrecioARSInput(e.target.value);
                const usd = (Number(valorDolar) || 0) > 0
                  ? parseDecimal(e.target.value) / Number(valorDolar) / unitsPerPack
                  : 0;
                onChange('priceUSD', Number(usd.toFixed(6)));
              }}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Cantidad minima (packs)</label>
            <input className="border rounded-lg px-3 py-2 w-full" type="number" min={1} placeholder="Ej: 1000" value={form.minQuantity} onChange={(e) => onChange('minQuantity', Number(e.target.value))} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Entrega (0 = inmediata, 1-30 dias)</label>
            <input className="border rounded-lg px-3 py-2 w-full" type="number" min={0} max={30} placeholder="Ej: 10" value={form.deliveryDays} onChange={(e) => onChange('deliveryDays', Number(e.target.value))} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Colores (separados por coma)</label>
            <input className="border rounded-lg px-3 py-2 w-full" placeholder="Ej: negro, blanco, azul" value={form.colors} onChange={(e) => onChange('colors', e.target.value)} />
          </div>
        </div>

        <label className="block text-sm font-medium text-gray-700">Descripcion</label>
        <textarea
          className="w-full border rounded-lg px-3 py-2"
          rows={4}
          placeholder="Detalle del producto, uso recomendado y observaciones"
          value={form.description}
          onChange={(e) => onChange('description', e.target.value)}
        />

        <div className="flex items-center gap-6 text-sm">
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={form.isFeatured} onChange={(e) => onChange('isFeatured', e.target.checked)} />
            Destacado
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={form.isActive} onChange={(e) => onChange('isActive', e.target.checked)} />
            Activo
          </label>          <label className="flex items-center gap-2">
            <input type="checkbox" checked={form.showPrice} onChange={(e) => onChange('showPrice', e.target.checked)} />
            Mostrar precio en el frontend
          </label>
        </div>

        <button
          type="submit"
          disabled={isSaving}
          className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-60"
        >
          <Save className="h-4 w-4 mr-2" />
          {isSaving ? 'Guardando...' : 'Guardar producto'}
        </button>
      </form>
    </div>
  );
};

export default NewProduct;
